import type { CreateSmartFieldV1MutationRequestApi } from '@ingenico/api-sdk-configuration-public';
import type { CoreError } from '@ingenico/api-sdk-core/common';
import type { CreateSmartFieldSchema } from '@ingenico/react-form-builder';
import { Level, showNotification } from '@ingenico/react-notifications';
import { useBusinessService } from '@ingenico/react-section-utils';
import type { MappedSmartFieldResourceApi } from '@ingenico/react-smartfield-utils';
import { enumSmartFieldDataType } from '@ingenico/react-smartfield-utils';
import { requestAndUpdateState } from '@ingenico/react-utils/functions';
import { useAsyncDataState } from '@ingenico/react-utils/hooks';
import { ALL_LNGS_NAMESPACES, LNGS_NAMESPACE_MODULES, LNGS_NAMESPACE_SMART_FIELDS } from '@ingenico/util-i18n-config';
import { useUpdateEffect } from '@react-hookz/web';
import { toString } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DeviceManagementService } from '../../../domain/business/services/deviceManagement.service';
import type { SmartFieldCategoryOption, SmartFieldsResource } from '../../../domain/models/smartFields.model';
import { useDeviceManagementCapability, useDeviceManagementError } from '../../../hooks';
import { useCreateSmartFieldCategoriesCalls } from '../../../hooks/useCreateSmartFieldCategoriesCalls.hook';
import styles from './ParameterModuleMapSmartFieldsModal.module.scss';
import { generateCreateSmartFieldBody } from './ParameterModuleMapSmartFieldsModal.util';

export interface useModuleDetailsSearchSmartFieldsModalParams {
  readonly onClose?: () => void;
  readonly onConfirm?: (newSmartField?: SmartFieldsResource) => void;
  readonly onConfirmCreation: (inputKey: string, newSmartField: MappedSmartFieldResourceApi) => void;
  readonly mode?: 'edit' | 'search';
  readonly selectedSmartField?: SmartFieldsResource;
  readonly hasError?: boolean;
  readonly handleChangeCategories: (newCategories: SmartFieldCategoryOption[]) => void;
  readonly categoryOptions: SmartFieldCategoryOption[];
  readonly isLoadingCategories: boolean;
  readonly selectedCategories: SmartFieldCategoryOption[];
  readonly inputKey: string;
  readonly handleCategorySearchChange: (search: string) => void;
  readonly parameterDataType?: keyof typeof enumSmartFieldDataType;
  readonly isOpen?: boolean;
  readonly handleSmartfieldSearchChange: (search: string) => void;
}

export function useModuleDetailsMapSmartFieldsModal({
  mode,
  onClose,
  onConfirm,
  onConfirmCreation,
  selectedSmartField,
  hasError,
  categoryOptions,
  isLoadingCategories,
  inputKey,
  handleChangeCategories,
  selectedCategories,
  handleCategorySearchChange,
  parameterDataType,
  isOpen,
  handleSmartfieldSearchChange,
}: useModuleDetailsSearchSmartFieldsModalParams) {
  const [selected, setSelected] = useState<SmartFieldsResource>();
  const [isCreation, setIsCreation] = useState(false);
  const [isFormValid, setFormValid] = useState<boolean>(false);
  const [smartFieldFormValues, setSmartFieldFormValues] = useState<CreateSmartFieldSchema | null>(null);
  const [_, setNewSmartFieldState] = useAsyncDataState<MappedSmartFieldResourceApi>();
  const [lockedIsStructureWide, setLockedIsStructureWide] = useState(false);

  const { t } = useTranslation([LNGS_NAMESPACE_MODULES.COMMON, LNGS_NAMESPACE_MODULES.DEFAULT]);
  const deviceManagementService = useBusinessService(DeviceManagementService);
  const { getErrorMessage } = useDeviceManagementError();

  useUpdateEffect(() => {
    setSelected(selectedSmartField);
  }, [selectedSmartField]);

  const handleFormValidation = useCallback(
    (params: { newSmartFieldWithNameAndDescriptionFormIsValid: boolean; smartFieldFormIsValid: boolean }) => {
      setFormValid(params.newSmartFieldWithNameAndDescriptionFormIsValid && params.smartFieldFormIsValid);
    },
    [],
  );

  const onFormChange = useCallback((form: CreateSmartFieldSchema) => setSmartFieldFormValues(form), []);

  const isCategoryFeatureEnabled = useDeviceManagementCapability('smartFields.listCategories');

  const {
    handleCategoryDelete,
    handleCategoryChange,
    categoryOptions: categoryCreationOptions,
    isLoading,
    handleSearchChange,
  } = useCreateSmartFieldCategoriesCalls(isCategoryFeatureEnabled);

  const creation = useMemo(
    () => ({
      categoryOptions: categoryCreationOptions,
      handleCategoryChange,
      handleCategoryDelete,
      handleSearchChange,
      isLoading,
    }),
    [categoryCreationOptions, handleCategoryChange, handleCategoryDelete, handleSearchChange, isLoading],
  );

  const handleConfirmCreation = useCallback(() => {
    if (smartFieldFormValues) {
      const body: CreateSmartFieldV1MutationRequestApi = {
        ...generateCreateSmartFieldBody(smartFieldFormValues),
        ...(smartFieldFormValues?.categories && smartFieldFormValues.categories.length > 0
          ? {
              categories: smartFieldFormValues.categories.map((category) => ({
                id: category.id,
                name: category.label,
              })),
            }
          : {}),
      };
      requestAndUpdateState(deviceManagementService.createSmartFieldV1(body), setNewSmartFieldState, {
        onSuccess: (newSmartField: MappedSmartFieldResourceApi) => {
          const mappedSmartField: SmartFieldsResource = {
            ...newSmartField,
            creationDate: newSmartField.creationDate ? toString(newSmartField.creationDate) : undefined,
          };
          onConfirmCreation(inputKey, newSmartField);
          onConfirm?.(mappedSmartField);
          showNotification({
            level: Level.Success,
            content: t('modal.createSmartField.successMessageToaster', { ns: ALL_LNGS_NAMESPACES.MODULES }),
          });
        },
        onError: (error: CoreError) => {
          showNotification({
            content: getErrorMessage(error),
            level: Level.Error,
          });
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    deviceManagementService,
    getErrorMessage,
    inputKey,
    onConfirmCreation,
    setNewSmartFieldState,
    smartFieldFormValues,
    t,
  ]);

  const handleConfirm = useCallback(() => {
    if (isCreation) {
      if (!isFormValid) {
        return;
      }

      handleConfirmCreation();
    } else {
      onConfirm?.(selected);
    }
  }, [handleConfirmCreation, isCreation, isFormValid, onConfirm, selected]);

  const handleRequestClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  const handleExited = useCallback(() => {
    setSelected(undefined);
    setLockedIsStructureWide(false);
  }, [setLockedIsStructureWide]);

  const handleChange = useCallback((newSmartField?: SmartFieldsResource) => {
    setSelected(newSmartField);
  }, []);

  const handleToggleCreation = useCallback(() => {
    setIsCreation((previous) => !previous);
    setFormValid(false);
  }, []);

  const isStructureWide = useMemo<boolean>(() => {
    return (
      selectedSmartField?.dataType === enumSmartFieldDataType.STRUCTURE ||
      parameterDataType === enumSmartFieldDataType.STRUCTURE
    );
  }, [parameterDataType, selectedSmartField?.dataType]);

  useEffect(() => {
    if (isOpen) {
      setLockedIsStructureWide(isStructureWide);
    } else {
      setIsCreation(false);
    }
  }, [isOpen, isStructureWide]);

  const customPaperClassName = useMemo<string>(
    () => (lockedIsStructureWide ? styles.tablePaper : styles.paper),
    [lockedIsStructureWide],
  );

  return {
    title:
      mode === 'edit'
        ? t('title.modal.changeSmartField', { ns: LNGS_NAMESPACE_MODULES.DEFAULT })
        : t('title.modal.mapSmartField', { ns: LNGS_NAMESPACE_MODULES.DEFAULT }),
    cancelButtonLabel: t('button.cancel', { ns: LNGS_NAMESPACE_MODULES.COMMON }),
    confirmButtonLabel: hasError
      ? t('button.retry', { ns: LNGS_NAMESPACE_MODULES.COMMON })
      : t('button.confirm', { ns: LNGS_NAMESPACE_MODULES.COMMON }),
    toggleCreationLabel: t('title.toggleCreation', { ns: LNGS_NAMESPACE_SMART_FIELDS.DEFAULT }),
    handleConfirm,
    handleRequestClose,
    handleExited,
    handleToggleCreation,
    isCreation,
    customPaperClassName,
    isStructureWide: lockedIsStructureWide,
    searchContent: {
      componentLabel: t('field.smartField', { ns: LNGS_NAMESPACE_MODULES.DEFAULT }),
      errorMessage: t('error.default', { ns: LNGS_NAMESPACE_MODULES.COMMON }),
      handleChange,
      placeholder: t('placeholder.smartField', { ns: LNGS_NAMESPACE_MODULES.DEFAULT }),
      selected,
    },
    categories: {
      categoriesLabel: t('fields.category', { ns: ALL_LNGS_NAMESPACES.SMART_FIELDS }),
      categoriesPlaceholder: t('fields.categories.placeholder', { ns: ALL_LNGS_NAMESPACES.SMART_FIELDS }),
      categoriesNoText: t('select.noOptions', { ns: ALL_LNGS_NAMESPACES.COMMON }),
      categoryOptions,
      handleChangeCategories,
      selectedCategories,
      isLoadingCategories,
      handleCategorySearchChange,
    },
    creation,
    handleFormValidation,
    onFormChange,
    isCategoryFeatureEnabled,
    isFormValid,
    handleSmartfieldSearchChange,
  };
}
